import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Center,
  Text,
  VStack,
  HStack,
  Badge,
  Icon,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ArrowUpIcon, ArrowDownIcon } from "@chakra-ui/icons";
import axios from "axios";
import proxy from "../../proxy";

// ─── Shared badge styles ─────────────────────────────────────
const BADGE_BASE = {
  px: 3,
  py: 1,
  borderRadius: "full",
  color: "white",
  fontSize: "xs",
  fontWeight: "bold",
};

const typeBadgeProps = (isOut) => ({
  ...BADGE_BASE,
  display: "inline-flex",
  alignItems: "center",
  gap: { base: 1.5, md: 2 },
  bg: isOut
    ? "linear-gradient(135deg,#FC8181,#FEB2B2)"
    : "linear-gradient(135deg,#48BB78,#68D391)",
  boxShadow: isOut
    ? "0 0 12px rgba(252,129,129,0.4)"
    : "0 0 12px rgba(72,187,120,0.4)",
});

const assetBadgeProps = {
  ...BADGE_BASE,
  bg: "linear-gradient(135deg,#9F7AEA,#B794F4)",
  boxShadow: "0 0 12px rgba(159,122,234,0.35)",
};

const walletBadgeProps = {
  ...BADGE_BASE,
  bg: "linear-gradient(135deg,#0BC5EA,#00B5D8)",
  boxShadow: "0 0 12px rgba(0,180,216,0.4)",
  display: "inline-flex",
  alignItems: "center",
  gap: { base: 1, md: 2 },
};

// ─── Date helpers ────────────────────────────────────────────
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff} days ago`;
  if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
  return formatDate(dateString);
};

// ─── Mobile Card ─────────────────────────────────────────────
const TransactionCard = ({ tx }) => {
  const isOut = Number(tx.out_in) === 0;

  return (
    <Box
      p={{ base: 2.5, sm: 4 }}
      bg="rgba(255,255,255,0.03)"
      borderRadius="xl"
      border="1px solid rgba(255,255,255,0.08)"
      transition="all 0.25s"
      _hover={{
        bg: "rgba(255,255,255,0.06)",
        transform: "translateY(-2px)",
        boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
      }}
    >
      {/* Header: Type + Date */}
      <HStack justify="space-between" mb={{ base: 2, sm: 3 }}>
        <Badge {...typeBadgeProps(isOut)} px={2} py={0.5} fontSize="2xs">
          <Icon as={isOut ? ArrowDownIcon : ArrowUpIcon} boxSize={2.5} />
          {isOut ? "Out" : "In"}
        </Badge>

        <Tooltip label={formatDate(tx.date_operation)} hasArrow>
          <Text fontSize="xs" color="gray.400" fontWeight="medium">
            {getRelativeTime(tx.date_operation)}
          </Text>
        </Tooltip>
      </HStack>

      {/* Amount + Asset */}
      <HStack justify="space-between" align="center">
        <Text
          fontSize={{ base: "lg", sm: "2xl" }}
          fontWeight="black"
          color={isOut ? "red.300" : "green.300"}
          textShadow={
            isOut
              ? "0 0 10px rgba(255,90,90,0.3)"
              : "0 0 10px rgba(90,255,140,0.3)"
          }
        >
          {isOut ? "-" : "+"}
          {tx.amount}
        </Text>

        <VStack spacing={1} align="end">
          <Badge {...assetBadgeProps} px={2} py={0.5} fontSize="2xs">
            {tx.symbol_crypto}
          </Badge>
          <Badge {...walletBadgeProps} px={2} py={0.5} fontSize="3xs">
            💾 Ledger
          </Badge>
        </VStack>
      </HStack>
    </Box>
  );
};

// ─── Desktop Table Row ───────────────────────────────────────
const TransactionRow = ({ tx }) => {
  const isOut = Number(tx.out_in) === 0;

  return (
    <Tr
      transition="0.25s"
      _hover={{
        bg: "rgba(255,255,255,0.05)",
        transform: "scale(1.005)",
        boxShadow: "0 8px 25px rgba(0,0,0,0.35)",
      }}
    >
      <Td color="gray.300" textAlign="center" fontSize="xs" py={2}>
        <Tooltip label={formatDate(tx.date_operation)} hasArrow>
          <Text fontWeight="medium">
            {getRelativeTime(tx.date_operation)}
          </Text>
        </Tooltip>
      </Td>

      <Td textAlign="center" py={2}>
        <Badge {...typeBadgeProps(isOut)} px={2} py={0.5} fontSize="2xs">
          <Icon as={isOut ? ArrowDownIcon : ArrowUpIcon} boxSize={2.5} />
          {isOut ? "Outflow" : "Inflow"}
        </Badge>
      </Td>

      <Td
        color={isOut ? "red.300" : "green.300"}
        fontWeight="extrabold"
        textAlign="center"
        fontSize="sm"
        py={2}
        textShadow={
          isOut
            ? "0 0 10px rgba(255,90,90,0.3)"
            : "0 0 10px rgba(90,255,140,0.3)"
        }
      >
        {isOut ? "-" : "+"}
        {tx.amount}
      </Td>

      <Td textAlign="center" py={2}>
        <Badge {...assetBadgeProps} px={2} py={0.5} fontSize="2xs">{tx.symbol_crypto}</Badge>
      </Td>

      <Td textAlign="center" py={2}>
        <Badge {...walletBadgeProps} px={2} py={0.5} fontSize="2xs">💾 Ledger</Badge>
      </Td>
    </Tr>
  );
};

// ─── Main Component ──────────────────────────────────────────
const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await axios.get(
          `${proxy}server_mts/Transactions.php`,
          { headers: { "Cache-Control": "no-cache" } }
        );

        if (!response.data || !Array.isArray(response.data)) {
          throw new Error("Invalid transactions data.");
        }

        setTransactions(response.data);
      } catch (err) {
        setError("Unable to retrieve the transactions.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  if (isLoading) {
    return (
      <Center minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="teal.300" thickness="4px" />
          <Text color="gray.400">Loading transactions...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="50vh">
        <VStack spacing={3}>
          <Text fontSize="5xl">⚠️</Text>
          <Text color="red.400" fontWeight="bold">
            {error}
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box maxW="900px" mx="auto" p={{ base: 2, sm: 4, md: 6 }}>
      <VStack spacing={{ base: 3, md: 8 }} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Heading
            size={{ base: "xs", md: "sm" }}
            color="white"
            letterSpacing="1px"
            mb={1}
            fontWeight="extrabold"
          >
            Recent Transactions
          </Heading>
          <Text
            color="whiteAlpha.600"
            fontSize={{ base: "2xs", md: "xs" }}
          >
            Latest inflows & outflows recorded in your wallet
          </Text>
        </Box>

        {/* Table Card */}
        <Box
          p={{ base: 2.5, sm: 4, md: 5 }}
          borderRadius={{ base: "xl", md: "2xl" }}
          bg="linear-gradient(135deg, rgba(26,32,44,0.97), rgba(45,55,72,0.97))"
          border="1px solid rgba(255,255,255,0.08)"
          backdropFilter="blur(18px)"
          boxShadow="0 15px 40px rgba(0,0,0,0.45)"
          overflowX="auto"
          position="relative"
        >
          {/* Animated Top Border */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="3px"
            bgGradient="linear(to-r, teal.300, cyan.400)"
            opacity={0.8}
            sx={{
              animation: "shine 3s ease-in-out infinite",
              "@keyframes shine": {
                "0%,100%": { opacity: 0.4 },
                "50%": { opacity: 1 },
              },
            }}
          />

          {/* Title */}
          <Text
            textAlign="center"
            mb={{ base: 3, md: 4 }}
            color="teal.200"
            fontSize={{ base: "2xs", md: "xs" }}
            fontWeight="bold"
            letterSpacing="1px"
          >
            📥 Latest {transactions.length} Transactions
          </Text>

          {/* Empty state */}
          {transactions.length === 0 ? (
            <Center py={10}>
              <VStack spacing={3}>
                <Text fontSize="3xl">📭</Text>
                <Text color="gray.400" fontSize="sm" fontWeight="medium">
                  No transactions recorded yet.
                </Text>
              </VStack>
            </Center>
          ) : isMobile ? (
            /* Mobile: Cards */
            <VStack spacing={{ base: 2, sm: 3 }} align="stretch">
              {transactions.map((tx, index) => (
                <TransactionCard key={index} tx={tx} />
              ))}
            </VStack>
          ) : (
            /* Desktop: Table */
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  {["Date", "Type", "Amount", "Asset", "Wallet"].map((h) => (
                    <Th
                      key={h}
                      color="teal.200"
                      fontSize="2xs"
                      textAlign="center"
                      py={2}
                      borderBottom="1px solid rgba(255,255,255,0.08)"
                    >
                      {h}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {transactions.map((tx, index) => (
                  <TransactionRow key={index} tx={tx} />
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default Transactions;